import { useTranslation } from 'react-i18next';
import { Head } from 'vite-react-ssg';
import { brand } from '../config/brand';
import { useLocale } from '../lib/use-locale';
import { Seo } from '../lib/seo';
import { PricingSection } from '../components/PricingSection';
import { ReviewsSection } from '../components/ReviewsSection';

import tenerfitLogo from '../assets/brand/tenerfit-logo.png';
import fisio01 from '../assets/services/fisioterapia/fisioterapia-01.jpg';
import fisio02 from '../assets/services/fisioterapia/fisioterapia-02.jpg';
import fisio03 from '../assets/services/fisioterapia/fisioterapia-03.jpg';
import fisioV01 from '../assets/services/fisioterapia/fisioterapia-v01.jpg';
import pilatesM01 from '../assets/services/pilates-maquina/pilates-maquina-01.jpg';
import pilatesM02 from '../assets/services/pilates-maquina/pilates-maquina-02.jpg';
import pilatesS01 from '../assets/services/pilates-suelo/pilates-suelo-01.jpg';
import pilatesS02 from '../assets/services/pilates-suelo/pilates-suelo-02.jpg';
import masaje01 from '../assets/services/masaje/masaje-01.jpg';
import bookingStep1 from '../assets/booking-guide/01-login.jpeg';
import bookingStep2 from '../assets/booking-guide/02-profesional.jpeg';
import bookingStep3 from '../assets/booking-guide/03-fecha-hora.jpeg';

const serviceImages: Record<string, string[]> = {
  fisioterapia: [fisioV01, fisio01, fisio02, fisio03],
  pilates: [pilatesM01, pilatesM02, pilatesS01, pilatesS02],
  masajes: [masaje01],
};

const whatsappIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style={{ marginRight: '0.5rem' }}
  >
    <path d="M12.04 2A9.94 9.94 0 0 0 2.1 11.95a9.89 9.89 0 0 0 1.35 4.98L2 22l5.23-1.37A9.94 9.94 0 1 0 12.04 2Zm5.82 14.12c-.24.68-1.42 1.3-1.96 1.38-.5.08-1.13.11-1.83-.11a16.7 16.7 0 0 1-1.65-.61 12.96 12.96 0 0 1-5-4.42 5.65 5.65 0 0 1-1.19-3.01c0-1.12.56-1.67.76-1.9a.82.82 0 0 1 .6-.28h.43c.14 0 .33-.05.51.39s.65 1.58.71 1.7c.06.1.1.24.02.38s-.12.22-.24.34-.24.27-.35.36c-.12.1-.24.22-.1.43s.6 1 1.29 1.61a7.7 7.7 0 0 0 1.87 1.16c.22.11.34.1.47-.06s.54-.63.68-.84c.14-.22.29-.18.48-.11s1.25.59 1.47.7c.22.11.36.16.41.25s.05.57-.19 1.24Z" />
  </svg>
);

export function Component() {
  const locale = useLocale();
  const { t } = useTranslation();

  const faqs = t('faq.items', { returnObjects: true }) as Array<{
    q: string;
    a: string;
  }>;

  // FAQPage rich-result schema lives on the home page (the canonical content hub).
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <Seo locale={locale} pageId="home" />
      <Head>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>
      <main id="main">
        {/* ===== Hero ===== */}
        <section className="hero-section">
          <div className="hero-section__videos" aria-hidden="true">
            <video
              src="/hero-1.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="hero-section__video"
            />
            <video
              src="/hero-2.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="hero-section__video"
            />
            <video
              src="/hero-3.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="hero-section__video"
            />
            <div className="hero-section__overlay" />
          </div>
          <div className="hero container" aria-labelledby="hero-title">
            <p className="hero__eyebrow">{t('hero.eyebrow')}</p>
            <h1 id="hero-title" className="hero__title">
              {t('hero.title')}
            </h1>
            <p className="hero__subtitle">{t('hero.subtitle')}</p>
            <div className="hero__actions">
              <a
                href={brand.contact.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="button button--primary button--lg"
              >
                {whatsappIcon}
                {t('hero.cta')}
              </a>
              <a
                href={brand.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="button button--secondary button--lg"
              >
                {t('services.ctaOnline')}
              </a>
              <a href="#services" className="button button--ghost">
                {t('hero.secondaryCta')}
              </a>
            </div>
          </div>
        </section>

        {/* ===== Google Reviews ===== */}
        <ReviewsSection />

        {/* ===== Services ===== */}
        <section id="services" className="services-section">
          <div className="services container" aria-labelledby="services-title">
            <header className="section-heading">
              <h2 id="services-title">{t('services.title')}</h2>
              <p>{t('services.subtitle')}</p>
            </header>
            <ul className="services__grid">
              {brand.services.map((s) => (
                <li key={s.id} className="service-card">
                  {serviceImages[s.id] && serviceImages[s.id].length > 0 && (
                    <div className="service-card__images">
                      {serviceImages[s.id].map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt={`${t(`services.${s.id}.title`)} ${i + 1}`}
                          className="service-card__img"
                          loading="lazy"
                          decoding="async"
                        />
                      ))}
                    </div>
                  )}
                  <div className="service-card__body">
                    <h3>{t(`services.${s.id}.title`)}</h3>
                    <p>{t(`services.${s.id}.description`)}</p>
                    <div className="service-card__ctas">
                      {s.id === 'pilates' ? (
                        <a
                          href={brand.contact.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="service-card__cta service-card__cta--whatsapp"
                        >
                          {t('services.ctaTrial')} →
                        </a>
                      ) : (
                        <>
                          <a
                            href={brand.contact.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="service-card__cta service-card__cta--whatsapp"
                          >
                            {t('services.ctaWhatsApp')} →
                          </a>
                          <a
                            href={brand.bookingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="service-card__cta service-card__cta--online"
                          >
                            {t('services.ctaOnline')} →
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ===== Booking guide ===== */}
        <section id="booking-guide" className="booking-guide-section">
          <div className="container booking-guide" aria-labelledby="booking-guide-title">
            <header className="section-heading">
              <h2 id="booking-guide-title">{t('bookingGuide.title')}</h2>
              <p>{t('bookingGuide.subtitle')}</p>
            </header>
            <ol className="booking-guide__steps">
              {[bookingStep1, bookingStep2, bookingStep3].map((src, i) => {
                const step = t(`bookingGuide.steps.${i}`, { returnObjects: true }) as {
                  title: string;
                  description: string;
                };
                return (
                  <li key={i} className="booking-guide__step">
                    <div className="booking-guide__step-image">
                      <img
                        src={src}
                        alt={t('bookingGuide.imageAlt', { n: i + 1 })}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="booking-guide__step-body">
                      <span className="booking-guide__step-number" aria-hidden="true">
                        {i + 1}
                      </span>
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="booking-guide__cta">
              <a
                href={brand.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="button button--primary button--lg"
              >
                {t('bookingGuide.cta')} →
              </a>
            </div>
          </div>
        </section>

        {/* ===== Sister brand: TenerFit ===== */}
        {/* data-nosnippet: keep this cross-promo block out of Google search snippets
            so Tenerfit copy is never used as TenerFisio's description. */}
        <section className="tenerfit-section" data-nosnippet="">
          <div className="container tenerfit">
            <div className="tenerfit__content">
              <img src={tenerfitLogo} alt="TenerFit" className="tenerfit__logo" />
              <h2>{t('tenerfit.title')}</h2>
              <p>{t('tenerfit.description')}</p>
              <a
                href="https://tenerfit.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="button button--primary"
              >
                {t('tenerfit.cta')}
              </a>
            </div>
          </div>
        </section>

        {/* ===== Our Space gallery — hidden until final photos are ready ===== */}
        {/* <Gallery /> */}

        {/* ===== Pricing ===== */}
        <PricingSection />

        {/* ===== FAQ ===== */}
        <section id="faq" className="faq-section">
          <div className="faq-home container" aria-labelledby="faq-title">
            <header className="section-heading">
              <h2 id="faq-title">{t('faq.title')}</h2>
              <p>{t('faq.intro')}</p>
            </header>
            <div className="faq-home__list">
              {faqs.map((item, i) => (
                <details key={i} className="faq-home__item" name="faq-home">
                  <summary className="faq-home__q">{item.q}</summary>
                  <div className="faq-home__a">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Contact ===== */}
        <section id="contact" className="contact-section">
          <div className="contact container" aria-labelledby="contact-title">
            <h2 id="contact-title">{t('contact.title')}</h2>

            <div className="contact__actions">
              <a href={`tel:${brand.contact.landline}`} className="contact__action-card">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.09 5.18 2 2 0 0 1 5.08 3h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 11.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
                </svg>
                <span className="contact__action-label">{t('contact.phoneLabel')}</span>
                <span className="contact__action-value">{brand.contact.landlineDisplay}</span>
              </a>
              <a
                href={brand.contact.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="contact__action-card contact__action-card--whatsapp"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12.04 2A9.94 9.94 0 0 0 2.1 11.95a9.89 9.89 0 0 0 1.35 4.98L2 22l5.23-1.37A9.94 9.94 0 1 0 12.04 2Zm5.82 14.12c-.24.68-1.42 1.3-1.96 1.38-.5.08-1.13.11-1.83-.11a16.7 16.7 0 0 1-1.65-.61 12.96 12.96 0 0 1-5-4.42 5.65 5.65 0 0 1-1.19-3.01c0-1.12.56-1.67.76-1.9a.82.82 0 0 1 .6-.28h.43c.14 0 .33-.05.51.39s.65 1.58.71 1.7c.06.1.1.24.02.38s-.12.22-.24.34-.24.27-.35.36c-.12.1-.24.22-.1.43s.6 1 1.29 1.61a7.7 7.7 0 0 0 1.87 1.16c.22.11.34.1.47-.06s.54-.63.68-.84c.14-.22.29-.18.48-.11s1.25.59 1.47.7c.22.11.36.16.41.25s.05.57-.19 1.24Z" />
                </svg>
                <span className="contact__action-label">WhatsApp</span>
                <span className="contact__action-value">{brand.contact.mobileDisplay}</span>
              </a>
            </div>

            <div className="contact__info">
              <dl className="contact__details">
                <div>
                  <dt>{t('contact.address')}</dt>
                  <dd>
                    <address>
                      {brand.address.street}, {brand.address.locality}
                    </address>
                  </dd>
                </div>
                <div className="contact__parking">
                  <dt>{t('contact.parkingLabel')}</dt>
                  <dd>{t('contact.parkingInfo')}</dd>
                </div>
                <div>
                  <dt>{t('contact.emailLabel')}</dt>
                  <dd>
                    <a href={`mailto:${brand.contact.email}`}>{brand.contact.email}</a>
                  </dd>
                </div>
                <div>
                  <dt>{t('contact.hoursLabel')}</dt>
                  <dd>{t('contact.hoursValue')}</dd>
                </div>
              </dl>
              <div className="contact__map">
                <iframe
                  title={t('contact.mapTitle', 'Location on Google Maps')}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(`${brand.name} ${brand.address.street}, ${brand.address.locality}`)}&z=17&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
